import axios from 'axios';
import { Contract, Interface, keccak256, toUtf8Bytes, AbiCoder, parseUnits, formatUnits } from 'ethers';
import { JOB_ABI, JOB_CONTRACT_ADDRESS, USDC_TOKEN_ABI, USDC_TOKEN_ADDRESS } from './Constants';
import { isMainnetMode } from './workloobNetwork';
import { WORKLOOB_NET } from './workloobNetwork';
import { withBaseProvider } from './baseRpc';
import API_URL from './Backendurl';

const ESCROW_READ_ABI = [
  'function nonces(address) view returns (uint256)',
  'function getOrderDetails(string) view returns (string,uint256,uint256,address,address,uint256,uint256[])',
  'function getJobDetails(string) view returns (string,uint256,uint256,address,address,uint256,uint256[])'
];

const jobDepositedIface = new Interface(JOB_ABI);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const parseJobDepositedChatIds = (receipt) => {
  const contract = JOB_CONTRACT_ADDRESS.toLowerCase();
  const chatIds = [];
  for (const log of receipt.logs || []) {
    if ((log.address || '').toLowerCase() !== contract) continue;
    try {
      const parsed = jobDepositedIface.parseLog(log);
      if (parsed?.name === 'JobDeposited') {
        const chatId = String(parsed.args.chatId ?? parsed.args[5] ?? '');
        if (chatId) chatIds.push(chatId);
      }
    } catch (_) {
      // ignore unrelated escrow logs
    }
  }
  return chatIds;
};

const getUsdcPermitVersion = async (token) => {
  try {
    if (typeof token.version === 'function') {
      return String(await token.version());
    }
  } catch (_) {
    // MockUSDC on testnet may not expose version()
  }
  return isMainnetMode() ? '2' : '1';
};

const postRelayerDeposit = async (payload) => {
  try {
    return await axios.post(`${API_URL}/api/v1/relayer/escrow/deposit`, payload);
  } catch (error) {
    const data = error?.response?.data;
    if (data?.error === 'Deposit verification failed' && data?.txHash && payload?.chatId) {
      await verifyDepositOnChain({
        txHash: data.txHash,
        chatId: payload.chatId,
        maxAttempts: 15
      });
      return { data: { txHash: data.txHash } };
    }
    throw error;
  }
};

/**
 * Verify deposit tx on configured network (Sepolia default; mainnet when env set).
 */
export async function verifyDepositOnMainnet({ txHash, chatId, maxAttempts = 8 }) {
  return verifyDepositOnChain({ txHash, chatId, maxAttempts });
}

/** Find the most recent JobDeposited tx for a chat when backend sync was missed. */
export async function findDepositTxForChatId({ chatId, fromBlock = 0 }) {
  return withBaseProvider(async (provider) => {
    const topic0 = jobDepositedIface.getEvent('JobDeposited').topicHash;
    const logs = await provider.getLogs({
      address: JOB_CONTRACT_ADDRESS,
      topics: [topic0],
      fromBlock,
      toBlock: 'latest'
    });

    for (let i = logs.length - 1; i >= 0; i--) {
      try {
        const parsed = jobDepositedIface.parseLog(logs[i]);
        const depositedChatId = String(parsed.args.chatId ?? parsed.args[5] ?? '');
        if (depositedChatId === String(chatId)) {
          return logs[i].transactionHash;
        }
      } catch (_) {
        // ignore unparsable logs
      }
    }
    return null;
  });
}

export async function verifyDepositOnChain({ txHash, chatId, maxAttempts = 8 }) {
  const chainId = WORKLOOB_NET.chainId;
  const networkLabel = WORKLOOB_NET.chainName;

  return withBaseProvider(async (provider) => {
    const code = await provider.getCode(JOB_CONTRACT_ADDRESS);
    if (!code || code === '0x') {
      throw new Error(
        `WorklobJOB has no code at ${JOB_CONTRACT_ADDRESS} on ${networkLabel}. Check contract address / network config.`
      );
    }

    let receipt = null;
    for (let i = 0; i < maxAttempts; i++) {
      receipt = await provider.getTransactionReceipt(txHash);
      if (receipt) break;
      await new Promise((r) => setTimeout(r, 1500));
    }

    if (!receipt) {
      throw new Error(
        `Transaction ${txHash} was not found on ${networkLabel} (chainId ${chainId}). ` +
          'Ensure backend BASE_RPC_URL and WORKLOOB_NETWORK match the frontend.'
      );
    }
    if (receipt.status !== 1) {
      throw new Error(`Deposit transaction reverted on ${networkLabel}: ${txHash}`);
    }
    if ((receipt.to || '').toLowerCase() !== JOB_CONTRACT_ADDRESS.toLowerCase()) {
      throw new Error(`Deposit tx was sent to ${receipt.to}, not the configured escrow contract.`);
    }
    if (!receipt.logs?.length) {
      throw new Error(
        `Deposit transaction has no event logs on ${networkLabel} — wrong contract or network.`
      );
    }

    const depositedChatIds = parseJobDepositedChatIds(receipt);
    if (depositedChatIds.length > 0) {
      if (!depositedChatIds.includes(String(chatId))) {
        throw new Error(
          `Deposit tx emitted JobDeposited for chatId ${depositedChatIds.join(', ')}, not ${chatId}.`
        );
      }
      return true;
    }

    const contract = new Contract(JOB_CONTRACT_ADDRESS, ESCROW_READ_ABI, provider);
    let lastErr = null;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        await contract.getOrderDetails.staticCall(String(chatId));
        return true;
      } catch (e) {
        lastErr = e;
        const msg = e?.reason || e?.shortMessage || e?.message || '';
        if (!msg.includes('Order does not exist')) {
          throw new Error(msg || `Could not read escrow order on ${networkLabel}`);
        }
        if (i < maxAttempts - 1) await sleep(1500);
      }
    }

    const msg = lastErr?.reason || lastErr?.shortMessage || lastErr?.message || '';
    throw new Error(
      msg.includes('Order does not exist')
        ? `Deposit tx is on ${networkLabel} but no escrow order for chatId ${chatId}.`
        : msg || `Could not read escrow order on ${networkLabel}`
    );
  });
}

// Backward-compatible name: now returns USDC "price" (defaults to 1).
export const getEthPriceUSD = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/v1/price/usdc`);
    return response.data.usd;
  } catch (error) {
    console.error('Failed to get USDC price from backend:', error);
    throw new Error('Unable to retrieve USDC price from backend');
  }
};

const getEscrowContract = (signerOrProvider) => {
  if (!signerOrProvider) {
    throw new Error('Escrow contract requires a signer or provider');
  }
  return new Contract(JOB_CONTRACT_ADDRESS, ESCROW_READ_ABI, signerOrProvider);
};

const getUsdcTokenContract = (signerOrProvider) => {
  if (!signerOrProvider) throw new Error('USDC contract requires signer or provider');
  return new Contract(USDC_TOKEN_ADDRESS, USDC_TOKEN_ABI, signerOrProvider);
};

const getUsdcDecimals = async (signerOrProvider) => {
  const token = getUsdcTokenContract(signerOrProvider);
  const decimals = await token.decimals();
  return Number(decimals);
};

const calculateUsdcAmount = async (usdAmount) => {
  // USDC is treated as ~1:1 with USD for platform flows.
  // Do NOT convert via a volatile "price" feed, otherwise approvals and deposits mismatch.
  const usdcPrice = 1;
  const usdcAmount = Number(usdAmount);
  return { usdcAmount, usdcPrice };
};

const coder = new AbiCoder();
const META_TX_DOMAIN = (chainId) => ({
  name: 'WorkloobEscrow',
  version: '1',
  chainId,
  verifyingContract: JOB_CONTRACT_ADDRESS
});

const TYPES = {
  MetaTx: [
    { name: 'from', type: 'address' },
    { name: 'actionHash', type: 'bytes32' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint256' }
  ]
};

const TYPEHASH = {
  Deposit: keccak256(toUtf8Bytes('Deposit(string jobId,string customerId,string talentId,address customerWallet,uint256 amount,string chatId)')),
  InProgress: keccak256(toUtf8Bytes('InProgress(string jobId,string customerId,string talentId,address talentWallet,string chatId)')),
  Complete: keccak256(toUtf8Bytes('Complete(string jobId,string customerId,string talentId,address talentWallet,string chatId)')),
  Disburse: keccak256(toUtf8Bytes('Disburse(string jobId,string customerId,string talentId,address customerWallet,uint256 amount,string chatId)')),
  Confirm: keccak256(toUtf8Bytes('Confirm(string jobId,string customerId,string talentId,address customerWallet,string chatId)'))
};

const hashString = (s) => keccak256(toUtf8Bytes(String(s)));

const actionHashDeposit = ({ jobId, customerId, talentId, customerWallet, amount, chatId }) =>
  keccak256(
    coder.encode(
      ['bytes32', 'bytes32', 'bytes32', 'bytes32', 'address', 'uint256', 'bytes32'],
      [TYPEHASH.Deposit, hashString(jobId), hashString(customerId), hashString(talentId), customerWallet, amount, hashString(chatId)]
    )
  );

const actionHashInProgress = ({ jobId, customerId, talentId, talentWallet, chatId }) =>
  keccak256(
    coder.encode(
      ['bytes32', 'bytes32', 'bytes32', 'bytes32', 'address', 'bytes32'],
      [TYPEHASH.InProgress, hashString(jobId), hashString(customerId), hashString(talentId), talentWallet, hashString(chatId)]
    )
  );

const actionHashComplete = ({ jobId, customerId, talentId, talentWallet, chatId }) =>
  keccak256(
    coder.encode(
      ['bytes32', 'bytes32', 'bytes32', 'bytes32', 'address', 'bytes32'],
      [TYPEHASH.Complete, hashString(jobId), hashString(customerId), hashString(talentId), talentWallet, hashString(chatId)]
    )
  );

const actionHashDisburse = ({ jobId, customerId, talentId, customerWallet, amount, chatId }) =>
  keccak256(
    coder.encode(
      ['bytes32', 'bytes32', 'bytes32', 'bytes32', 'address', 'uint256', 'bytes32'],
      [TYPEHASH.Disburse, hashString(jobId), hashString(customerId), hashString(talentId), customerWallet, amount, hashString(chatId)]
    )
  );

const actionHashConfirm = ({ jobId, customerId, talentId, customerWallet, chatId }) =>
  keccak256(
    coder.encode(
      ['bytes32', 'bytes32', 'bytes32', 'bytes32', 'address', 'bytes32'],
      [TYPEHASH.Confirm, hashString(jobId), hashString(customerId), hashString(talentId), customerWallet, hashString(chatId)]
    )
  );

const signMetaTx = async ({ signer, from, actionHash, deadline = 0 }) => {
  const provider = signer.provider;
  if (!provider) throw new Error('Signer provider missing');
  const { chainId } = await provider.getNetwork();
  const contract = getEscrowContract(provider);
  const nonce = await contract.nonces(from);

  const value = { from, actionHash, nonce: nonce.toString(), deadline };
  return signer.signTypedData(META_TX_DOMAIN(Number(chainId)), TYPES, value);
};

export const depositEscrow = async ({
  signer,
  jobId,
  customerId,
  talentId,
  customerWallet,
  chatId,
  usdAmount,
  // Optional: pass in ETH balance from Web3Context to decide
  // whether to request a relayer "gas drip" before approve().
  ethBalance
}) => {
  const { usdcAmount, usdcPrice } = await calculateUsdcAmount(usdAmount);
  const decimals = await getUsdcDecimals(signer);
  const amount = parseUnits(Number(usdcAmount).toFixed(decimals), decimals);

  // Deposit intent signature (meta-tx). Relayer will submit the tx.
  const token = getUsdcTokenContract(signer);
  const { chainId } = await signer.provider.getNetwork();

  // Preflight: fail early with clear messages if balance is insufficient.
  try {
    const bal = await token.balanceOf(customerWallet);
    if (bal < amount) {
      throw new Error(
        `Insufficient USDC balance. Required ${Number(usdcAmount).toFixed(2)} USDC, available ${Number(formatUnits(bal, decimals)).toFixed(2)} USDC.`
      );
    }
  } catch (e) {
    if (e instanceof Error) throw e;
    throw new Error('Unable to verify USDC balance');
  }

  const intentSignature = await signMetaTx({
    signer,
    from: customerWallet,
    actionHash: actionHashDeposit({ jobId, customerId, talentId, customerWallet, amount, chatId }),
    deadline: 0
  });

  // Best UX: try Permit (gasless approval) first; if it fails for any reason,
  // fall back to gas-drip + approve() + depositWithSig.
  let response;
  try {
    if (typeof token.nonces !== 'function') {
      throw new Error('Permit not supported: token.nonces missing');
    }

    const permitNonce = await token.nonces(customerWallet);
    const permitDeadline = Math.floor(Date.now() / 1000) + 60 * 30; // 30 minutes

    const permitDomain = {
      name: await token.name(),
      version: await getUsdcPermitVersion(token),
      chainId: Number(chainId),
      verifyingContract: USDC_TOKEN_ADDRESS
    };
    const permitTypes = {
      Permit: [
        { name: 'owner', type: 'address' },
        { name: 'spender', type: 'address' },
        { name: 'value', type: 'uint256' },
        { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' }
      ]
    };
    const permitValue = {
      owner: customerWallet,
      spender: JOB_CONTRACT_ADDRESS,
      value: amount.toString(),
      nonce: permitNonce.toString(),
      deadline: permitDeadline
    };

    const permitSig = await signer.signTypedData(permitDomain, permitTypes, permitValue);
    const sig = permitSig.startsWith('0x') ? permitSig.slice(2) : permitSig;
    const r = `0x${sig.slice(0, 64)}`;
    const s = `0x${sig.slice(64, 128)}`;
    const v = parseInt(sig.slice(128, 130), 16);

    response = await postRelayerDeposit({
      jobId,
      customerId,
      talentId,
      customerWallet,
      amount: amount.toString(),
      chatId,
      // permit
      permitDeadline,
      v,
      r,
      s,
      // intent
      intentDeadline: 0,
      intentSignature
    });
  } catch (permitError) {
    // Fallback: user approves allowance (requires gas), then relayer calls depositWithSig.
    // We can optionally drip ETH to the caller wallet so they don't pay fees.
    console.warn('Permit deposit path failed, falling back to approve:', permitError?.message || permitError);

    const hasEthForGas = Number(ethBalance || 0) > 0.00002;

    // Only approve if allowance is insufficient (avoid repeated popups).
    let allowanceOk = false;
    try {
      if (typeof token.allowance === 'function') {
        const currentAllowance = await token.allowance(customerWallet, JOB_CONTRACT_ADDRESS);
        allowanceOk = currentAllowance >= amount;
      }
    } catch (e) {
      allowanceOk = false;
    }

    if (!allowanceOk) {
      // If user has no ETH for gas, ask relayer to top up first.
      if (!hasEthForGas) {
        try {
          await axios.post(`${API_URL}/api/v1/relayer/gas-drip`, {
            walletAddress: customerWallet
          });
        } catch (e) {
          // Non-fatal: user might already have gas, drip may be rate-limited, etc.
        }
      }

      const approveTx = await token.approve(JOB_CONTRACT_ADDRESS, amount);
      await approveTx.wait();
    }

    const postNoPermit = () =>
      postRelayerDeposit({
        jobId,
        customerId,
        talentId,
        customerWallet,
        amount: amount.toString(),
        chatId,
        deadline: 0,
        signature: intentSignature
      });

    try {
      response = await postNoPermit();
    } catch (e) {
      const code = e?.response?.data?.error;

      // If backend tells us allowance is insufficient (or we got the OZ custom error),
      // force an approve and retry once. This fixes cases where allowance checks were skipped
      // or the user swapped wallets / network mid-flow.
      const revertData = e?.response?.data?.revertData;
      const isInsufficientAllowance =
        code === 'INSUFFICIENT_ALLOWANCE' ||
        (typeof revertData === 'string' && revertData.startsWith('0xfb8f41b2')) ||
        (typeof e?.response?.data?.message === 'string' &&
          e.response.data.message.toLowerCase().includes('allowance'));

      if (!isInsufficientAllowance) throw e;

      if (!hasEthForGas) {
        try {
          await axios.post(`${API_URL}/api/v1/relayer/gas-drip`, {
            walletAddress: customerWallet
          });
        } catch (_) {
          // ignore
        }
      }

      const approveTx = await token.approve(JOB_CONTRACT_ADDRESS, amount);
      await approveTx.wait();

      response = await postNoPermit();
    }
  }

  return { relayed: true, txHash: response.data.txHash, usdcAmount, usdcPrice };
};

export const inProgressEscrow = async ({
  signer,
  jobId,
  customerId,
  talentId,
  talentWallet,
  chatId
}) => {
  try {
    const signature = await signMetaTx({
      signer,
      from: talentWallet,
      actionHash: actionHashInProgress({ jobId, customerId, talentId, talentWallet, chatId }),
      deadline: 0
    });

    const response = await axios.post(`${API_URL}/api/v1/relayer/escrow/in-progress`, {
      jobId,
      customerId,
      talentId,
      talentWallet,
      chatId,
      deadline: 0,
      signature
    });

    return { relayed: true, txHash: response.data.txHash };
  } catch (error) {
    console.error('Error in inProgressEscrow:', error);
    if (error.reason) {
      throw new Error(`Contract error: ${error.reason}`);
    }
    if (error.data?.message) {
      throw new Error(`Contract error: ${error.data.message}`);
    }
    if (error.message) {
      throw new Error(`Transaction error: ${error.message}`);
    }
    throw error;
  }
};

export const completeEscrow = async ({
  signer,
  jobId,
  customerId,
  talentId,
  talentWallet,
  chatId
}) => {
  try {
    const signature = await signMetaTx({
      signer,
      from: talentWallet,
      actionHash: actionHashComplete({ jobId, customerId, talentId, talentWallet, chatId }),
      deadline: 0
    });

    const response = await axios.post(`${API_URL}/api/v1/relayer/escrow/complete`, {
      jobId,
      customerId,
      talentId,
      talentWallet,
      chatId,
      deadline: 0,
      signature
    });

    return { relayed: true, txHash: response.data.txHash };
  } catch (error) {
    console.error('Error in completeEscrow:', error);
    // Extract revert reason if available
    if (error.reason) {
      throw new Error(`Contract error: ${error.reason}`);
    }
    if (error.data?.message) {
      throw new Error(`Contract error: ${error.data.message}`);
    }
    if (error.message) {
      throw new Error(`Transaction error: ${error.message}`);
    }
    throw error;
  }
};

// Helper function to verify job status after transaction
export const verifyJobStatus = async ({
  signer,
  jobId,
  expectedStatus
}) => {
  try {
    const contract = getEscrowContract(signer);
    const job = await contract.jobs(jobId);
    const currentStatus = job.status?.toString();
    console.log(`Job ${jobId} status: ${currentStatus} (Expected: ${expectedStatus})`);
    return {
      status: currentStatus,
      matches: currentStatus === String(expectedStatus),
      job
    };
  } catch (error) {
    console.error('Error verifying job status:', error);
    throw error;
  }
};

export const confirmEscrow = async ({
  signer,
  jobId,
  customerId,
  talentId,
  customerWallet,
  chatId
}) => {
  try {
    const signature = await signMetaTx({
      signer,
      from: customerWallet,
      actionHash: actionHashConfirm({ jobId, customerId, talentId, customerWallet, chatId }),
      deadline: 0
    });

    const response = await axios.post(`${API_URL}/api/v1/relayer/escrow/confirm`, {
      jobId,
      customerId,
      talentId,
      customerWallet,
      chatId,
      deadline: 0,
      signature
    });

    return { relayed: true, txHash: response.data.txHash };
  } catch (error) {
    console.error('Error in confirmEscrow:', error);
    // Extract revert reason if available
    if (error.reason) {
      throw new Error(`Contract error: ${error.reason}`);
    }
    if (error.data?.message) {
      throw new Error(`Contract error: ${error.data.message}`);
    }
    if (error.message) {
      throw new Error(`Transaction error: ${error.message}`);
    }
    throw error;
  }
};

export const disburseEscrow = async ({
  signer,
  jobId,
  customerId,
  talentId,
  customerWallet,
  amount,
  chatId
}) => {
  try {
    const signature = await signMetaTx({
      signer,
      from: customerWallet,
      actionHash: actionHashDisburse({ jobId, customerId, talentId, customerWallet, amount, chatId }),
      deadline: 0
    });

    const response = await axios.post(`${API_URL}/api/v1/relayer/escrow/disburse`, {
      jobId,
      customerId,
      talentId,
      customerWallet,
      amount: amount.toString(),
      chatId,
      deadline: 0,
      signature
    });

    return { relayed: true, txHash: response.data.txHash };
  } catch (error) {
    console.error('Error in disburseEscrow:', error);
    if (error.reason) {
      throw new Error(`Contract error: ${error.reason}`);
    }
    if (error.data?.message) {
      throw new Error(`Contract error: ${error.data.message}`);
    }
    if (error.message) {
      throw new Error(`Transaction error: ${error.message}`);
    }
    throw error;
  }
};

export const getJobDetails = async ({
  signer,
  chatId,
  jobId // Keep for backward compatibility but prefer chatId
}) => {
  try {
    const contract = getEscrowContract(signer);
    
    // Use chatId if provided, otherwise fall back to jobId (for backward compatibility)
    const identifier = chatId || jobId;
    if (!identifier) {
      throw new Error('Either chatId or jobId must be provided');
    }
    
    console.log('Fetching order details for chatId:', chatId || 'N/A', 'jobId:', jobId || 'N/A');
    
    // Try getOrderDetails first (new method), fall back to getJobDetails (legacy)
    let details;
    try {
      details = await contract.getOrderDetails(identifier);
    } catch (e) {
      const msg = e?.reason || e?.shortMessage || e?.message || '';
      if (String(msg).includes('Order does not exist')) {
        return null;
      }
      // Fall back to legacy method if getOrderDetails is missing from ABI
      console.log('getOrderDetails not available, using getJobDetails');
      try {
        details = await contract.getJobDetails(identifier);
      } catch (e2) {
        const msg2 = e2?.reason || e2?.shortMessage || e2?.message || '';
        if (String(msg2).includes('Order does not exist')) {
          return null;
        }
        throw e2;
      }
    }
    
    // Ethers returns multiple values as both an array (0..n) and named props only
    // when the ABI contains names. Be defensive across both shapes.
    const jobIdValue = details?.jobId ?? details?.[0];
    const totalAmountValue = details?.totalAmount ?? details?.[1];
    const remainingAmountValue = details?.remainingAmount ?? details?.[2];
    const customerWalletValue = details?.customerWallet ?? details?.[3];
    const talentWalletValue = details?.talentWallet ?? details?.[4];
    const statusValue = details?.status ?? details?.[5];
    const disbursedValue = details?.disbursedAmounts ?? details?.[6] ?? [];

    return {
      jobId: String(jobIdValue ?? ''),
      totalAmount: (totalAmountValue ?? 0n).toString(),
      remainingAmount: (remainingAmountValue ?? 0n).toString(),
      customerWallet: String(customerWalletValue ?? ''),
      talentWallet: String(talentWalletValue ?? ''),
      status: (statusValue ?? 0n).toString(),
      disbursedAmounts: Array.isArray(disbursedValue)
        ? disbursedValue.map((amt) => (amt ?? 0n).toString())
        : []
    };
  } catch (error) {
    console.error('Error fetching job details:', error);
    if (error.reason) {
      throw new Error(`Contract error: ${error.reason}`);
    }
    if (error.data?.message) {
      throw new Error(`Contract error: ${error.data.message}`);
    }
    if (error.message) {
      throw new Error(`Query error: ${error.message}`);
    }
    throw error;
  }
};
